import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const CW = 1024;
const CH = 1024;
const API_BASE = '/api/annotate';

function QualifyPage() {
  const [searchParams] = useSearchParams();
  const canvasRef = useRef(null);

  const prolificPid = searchParams.get('prolificPid') || searchParams.get('PROLIFIC_PID') || '';
  const studyId = searchParams.get('studyId') || searchParams.get('STUDY_ID') || '';
  const sessionId = searchParams.get('sessionId') || searchParams.get('SESSION_ID') || '';

  const [scenes, setScenes] = useState([]);
  const [sceneIdx, setSceneIdx] = useState(0);
  const [bgImage, setBgImage] = useState(null);
  const [objImages, setObjImages] = useState({});
  const [items, setItems] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState('Loading...');
  const [currentEnv, setCurrentEnv] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Phase: 'rate' for gold standard, 'edit' for arrangement
  const currentScene = scenes[sceneIdx] || {};
  const mode = currentScene._mode || 'rate';
  const totalRate = scenes.filter(s => s._mode === 'rate').length;
  const totalEdit = scenes.filter(s => s._mode === 'edit').length;
  const rateIdx = scenes.slice(0, sceneIdx + 1).filter(s => s._mode === 'rate').length;
  const editIdx = scenes.slice(0, sceneIdx + 1).filter(s => s._mode === 'edit').length;

  useEffect(() => {
    const seed = prolificPid || 'default';
    fetch(`${API_BASE}/suggested_scenes?batchStart=0&batchSize=50&seed=${seed}&qualify=true`)
      .then(r => r.json()).then(d => {
        const s = d.scenes || [];
        setScenes(s);
        if (s.length > 0) loadScene(s[0]);
        else setStatus('No scenes');
      });
  }, []);

  const loadBg = (name) => new Promise(resolve => {
    const img = new Image(); img.crossOrigin = 'anonymous';
    img.onload = () => { setBgImage(img); resolve(img); };
    img.src = `${API_BASE}/scene/${name}`;
  });

  const loadObjImg = (label) => new Promise(resolve => {
    if (objImages[label]) { resolve(objImages[label]); return; }
    const img = new Image(); img.crossOrigin = 'anonymous';
    img.onload = () => { setObjImages(prev => ({ ...prev, [label]: img })); resolve(img); };
    img.onerror = () => resolve(null);
    img.src = `${API_BASE}/object/${label}`;
  });

  const loadScene = async (scene) => {
    setCurrentEnv((scene.environment || '').replace(/_/g, ' '));
    setSelectedIdx(-1);
    await loadBg(scene.scene || 'hardwood_room');
    await Promise.all((scene.items || []).map(i => loadObjImg(i.label)));
    setItems((scene.items || []).map((item, i) => ({
      ...item, type: item.type || 'bg_object', rotation: item.rotation || 0,
      aspect: item.aspect || 100, flipH: item.flipH || false, layer: item.layer || i,
    })));
    setStatus('');
  };

  const draw = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CW, CH);
    if (bgImage) ctx.drawImage(bgImage, 0, 0, CW, CH);
    else { ctx.fillStyle = '#333'; ctx.fillRect(0, 0, CW, CH); }
    [...items].map((item, i) => ({ ...item, _idx: i })).sort((a, b) => (a.layer || 0) - (b.layer || 0)).forEach(item => {
      const img = objImages[item.label]; if (!img) return;
      const h = CH * item.scale / 100, w = h * (img.width / img.height);
      const cx = CW * item.x / 100, cy = CH * item.y / 100;
      ctx.drawImage(img, cx - w/2, cy - h/2, w, h);
      if (item._idx === selectedIdx) {
        ctx.strokeStyle = '#00ff00'; ctx.lineWidth = 2; ctx.setLineDash([6, 3]);
        ctx.strokeRect(cx - w/2 - 2, cy - h/2 - 2, w + 4, h + 4); ctx.setLineDash([]);
      }
    });
  }, [items, bgImage, objImages, selectedIdx]);

  useEffect(() => { draw(); }, [draw]);

  const hitTest = useCallback((mx, my) => {
    for (let i = items.length - 1; i >= 0; i--) {
      const item = items[i], img = objImages[item.label]; if (!img) continue;
      const h = CH * item.scale / 100, w = h * (img.width / img.height);
      const cx = CW * item.x / 100, cy = CH * item.y / 100;
      if (mx >= cx-w/2 && mx <= cx+w/2 && my >= cy-h/2 && my <= cy+h/2) return i;
    }
    return -1;
  }, [items, objImages]);

  const getCoords = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - rect.left) * CW / rect.width, y: (e.clientY - rect.top) * CH / rect.height };
  };

  const onMouseDown = (e) => {
    if (mode === 'rate') return;
    const { x, y } = getCoords(e); const idx = hitTest(x, y); setSelectedIdx(idx);
    if (idx >= 0) { setDragging(true); setDragOffset({ x: x - CW * items[idx].x / 100, y: y - CH * items[idx].y / 100 }); }
  };
  const onMouseMove = (e) => {
    if (!dragging || selectedIdx < 0) return;
    const { x, y } = getCoords(e);
    setItems(prev => prev.map((item, i) => i === selectedIdx ? { ...item, x: (x - dragOffset.x) / CW * 100, y: (y - dragOffset.y) / CH * 100 } : item));
  };
  const onMouseUp = () => setDragging(false);

  const scaleSelected = (d) => { if (selectedIdx < 0) return; setItems(prev => prev.map((it, i) => i === selectedIdx ? { ...it, scale: Math.max(2, Math.min(90, it.scale + d)) } : it)); };
  const deleteSelected = () => { if (selectedIdx < 0) return; setItems(prev => prev.filter((_, i) => i !== selectedIdx)); setSelectedIdx(-1); };

  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || mode === 'rate') return;
      if (e.key === 'Delete' || e.key === 'Backspace') { deleteSelected(); e.preventDefault(); }
      if (e.key === '=' || e.key === '+') { scaleSelected(2); e.preventDefault(); }
      if (e.key === '-') { scaleSelected(-2); e.preventDefault(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const advance = () => {
    const next = sceneIdx + 1;
    if (next >= scenes.length) {
      setSubmitted(true);
      if (prolificPid) window.location.href = 'https://app.prolific.com/submissions/complete?cc=VIGR_COMPLETE';
      return;
    }
    setSceneIdx(next);
    loadScene(scenes[next]);
  };

  const submitRate = (verdict) => {
    const scene = scenes[sceneIdx];
    fetch(`${API_BASE}/label`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        scene: scene.scene, verdict, items: scene.items, phase: 'qualify_rate',
        workerId: prolificPid, hitId: studyId, assignmentId: sessionId,
        _gold: scene._gold || false, _expected: scene._expected || '',
      }),
    });
    advance();
  };

  const submitEdit = () => {
    const sceneData = {
      scene: currentScene.scene || 'hardwood_room',
      items: items.map(({ label, x, y, scale, rotation, aspect, flipH, layer, type }) =>
        ({ type: type || 'bg_object', label, x, y, scale, rotation: rotation || 0, aspect: aspect || 100, flipH: flipH || false, layer: layer || 0 })),
    };
    // CNN soft gate
    setSubmitting(true); setStatus('Checking...');
    fetch(`${API_BASE}/classify_cnn`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(sceneData) })
      .then(r => r.json()).then(clf => {
        setSubmitting(false);
        // Save regardless, but mark if CNN rejected
        fetch(`${API_BASE}/label`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...sceneData, verdict: 'good', phase: 'qualify_edit',
            workerId: prolificPid, hitId: studyId, assignmentId: sessionId,
            cnn_prediction: clf.prediction, cnn_confidence: clf.confidence }),
        });
        if (clf.prediction === 'bad') {
          setStatus('Our checker thinks this needs more work. Keep adjusting or click Skip.');
          return;
        }
        setStatus('Submitted!'); advance();
      }).catch(() => { setSubmitting(false); advance(); });
  };

  if (submitted) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'system-ui' }}>
      <div style={{ textAlign: 'center' }}><h2>Thank you!</h2><p>Your responses have been submitted.</p></div>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: 'system-ui, sans-serif', background: '#f5f5f5' }}>
      <div style={{ width: 300, background: 'white', borderRight: '1px solid #e0e0e0', padding: '1rem', overflowY: 'auto', flexShrink: 0 }}>
        {/* Progress */}
        <div style={{ marginBottom: '1rem', padding: '0.5rem', background: mode === 'rate' ? '#fff8e1' : '#e3f2fd', borderRadius: 8, fontSize: '0.85rem' }}>
          {mode === 'rate' ? (
            <><strong>Part 1: Rating</strong> ({rateIdx}/{totalRate})</>
          ) : (
            <><strong>Part 2: Arranging</strong> ({editIdx}/{totalEdit})</>
          )}
          <div style={{ marginTop: 4, height: 4, background: '#ccc', borderRadius: 2 }}>
            <div style={{ height: '100%', background: mode === 'rate' ? '#ff9800' : '#2196f3', borderRadius: 2,
              width: `${(sceneIdx / Math.max(scenes.length, 1)) * 100}%`, transition: 'width 0.3s' }} />
          </div>
        </div>

        {currentEnv && <div style={{ fontSize: '0.8rem', color: '#1565c0', marginBottom: '0.5rem', padding: '4px 8px', background: '#e3f2fd', borderRadius: 4 }}>
          Scene type: <strong>{currentEnv}</strong></div>}

        {mode === 'rate' ? (
          <div>
            <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.75rem', lineHeight: 1.6 }}>
              Does this look like a realistic room? Objects should be on the floor at natural sizes.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => submitRate('good')} style={{ flex: 1, padding: '0.7rem', fontSize: '1rem', fontWeight: 600, borderRadius: 8, border: 'none', background: '#4caf50', color: 'white', cursor: 'pointer' }}>Good</button>
              <button onClick={() => submitRate('bad')} style={{ flex: 1, padding: '0.7rem', fontSize: '1rem', fontWeight: 600, borderRadius: 8, border: 'none', background: '#f44336', color: 'white', cursor: 'pointer' }}>Bad</button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: '0.8rem', color: '#555', marginBottom: '0.5rem', lineHeight: 1.6 }}>
              Drag objects to realistic positions. Use +/- to resize, Delete to remove.
            </div>
            {selectedIdx >= 0 && items[selectedIdx] && (
              <div style={{ marginBottom: '0.5rem', padding: '0.5rem', background: '#f0f7ff', borderRadius: 6 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>{items[selectedIdx].label.replace(/_/g, ' ')}</div>
                <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: 2 }}>Scale: {items[selectedIdx].scale.toFixed(0)}%</div>
                <input type="range" min="2" max="90" value={items[selectedIdx].scale}
                  onChange={e => setItems(prev => prev.map((it, i) => i === selectedIdx ? { ...it, scale: parseFloat(e.target.value) } : it))}
                  style={{ width: '100%' }} />
                <button onClick={deleteSelected} style={{ padding: '2px 8px', fontSize: '0.8rem', borderRadius: 4, border: '1px solid #c33', background: 'white', color: '#c33', cursor: 'pointer' }}>Delete</button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={submitEdit} disabled={submitting}
                style={{ flex: 1, padding: '0.7rem', fontSize: '1rem', fontWeight: 600, borderRadius: 8, border: 'none', background: submitting ? '#aaa' : '#4caf50', color: 'white', cursor: submitting ? 'wait' : 'pointer' }}>
                {submitting ? 'Checking...' : 'Submit'}
              </button>
              <button onClick={advance}
                style={{ padding: '0.7rem', fontSize: '0.85rem', borderRadius: 8, border: '1px solid #ccc', background: 'white', cursor: 'pointer' }}>
                Skip
              </button>
            </div>
          </div>
        )}
        <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '0.5rem' }}>{status}</div>
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
        <canvas ref={canvasRef} width={CW} height={CH}
          style={{ maxWidth: '100%', maxHeight: 'calc(100vh - 2rem)', border: '1px solid #ccc', borderRadius: 4,
                   cursor: mode === 'rate' ? 'default' : dragging ? 'grabbing' : 'default' }}
          onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp} />
      </div>
    </div>
  );
}

export default QualifyPage;
